"use client"

import { useState, useContext, useEffect, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  Paper,
  Chip,
} from "@mui/material"

import SearchIcon from "@mui/icons-material/Search"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import WarningIcon from "@mui/icons-material/Warning"
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ClearIcon from "@mui/icons-material/Clear"
import PersonIcon from "@mui/icons-material/Person"

const BuscadorItemsUbicaciones = ({ onSearch, vieneDe }) => {
  const theme = useTheme()

  const {
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    ubicaciones,
    getUbicacion,
    isNavbarCollapsed,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
  } = useContext(ErpContext)

  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [precioItem, setPrecioItem] = useState("")
  const [searchText, setSearchText] = useState("")
  const [selectedItem, setSelectedItem] = useState(null)
  const [soloStockBajo, setSoloStockBajo] = useState(false)
  const [mostrarUltimosCreados, setMostrarUltimosCreados] = useState(false)
  const [mostrarRecetas, setMostrarRecetas] = useState(false)
  const [selectedProveedor, setSelectedProveedor] = useState(null)
  const [searchTextProveedor, setSearchTextProveedor] = useState("")

  const autocompleteRef = useRef(null)
  const isFirstRender = useRef(true)

  const generateItemLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")
    return `${item.codigo} - ${item.descripcion} - ${attributeValues} - ${item.codigoScanner}`
  }

  useEffect(() => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(searchText)
    } else {
      arrayItemTresLetras([])
    }
  }, [searchText])

  // Búsqueda de proveedores (tipo entidad 2)
  useEffect(() => {
    if (searchTextProveedor.length >= 3) {
      getEntidadesClientesTresLetras(searchTextProveedor, [2])
    }
  }, [searchTextProveedor])

  useEffect(() => {
    if (searchText.length >= 3 && itemTresLetras.length > 0) {
      const found = itemTresLetras.find((item) => item.codigoScanner === searchText)
      if (found) {
        const autoSelected = {
          label: generateItemLabel(found),
          descripcion: found.descripcion,
          codigo: found.codigo,
          id: found.id,
          codigoScanner: found.codigoScanner,
        }
        setSelectedItem(autoSelected)
      }
    }
  }, [searchText, itemTresLetras])

  useEffect(() => {
    const fetchData = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }
      try {
        await getUbicacion()
        await getItemAtributoNoEliminados()
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  const handleItemChange = (event, newValue) => {
    setSelectedItem(newValue)
  }

  const handleFilter = () => {
    // Nueva funcionalidad: si hay texto de búsqueda pero no hay item seleccionado,
    // realizar búsqueda múltiple
    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null;
    
    const filters = {
      itemId: selectedItem ? selectedItem.id : null,
      ubicacionId: selectedUbicacion ? selectedUbicacion.id : null,
      precio: precioItem !== "" ? Number(precioItem) : null,
      soloStockBajo,
      mostrarUltimosCreados,
      mostrarRecetas,
      proveedorId: selectedProveedor ? selectedProveedor.id : null,
      searchText: searchTextForMultiple, // Nuevo parámetro para búsqueda múltiple
    }
    // console.log("Filtros enviados desde Buscador:", filters)
    onSearch(filters)
  }

  // Función para resetear todos los campos a sus valores iniciales
  const handleReset = () => {
    setSelectedUbicacion(null)
    setPrecioItem("")
    setSearchText("")
    setSelectedItem(null)
    setSoloStockBajo(false)
    setMostrarUltimosCreados(false)
    setMostrarRecetas(false)
    setSelectedProveedor(null)
    setSearchTextProveedor("")

    // Enviar filtros vacíos para resetear la búsqueda
    onSearch({
      itemId: null,
      ubicacionId: null,
      precio: null,
      soloStockBajo: false,
      mostrarUltimosCreados: false,
      mostrarRecetas: false,
      proveedorId: null,
      searchText: null, // Resetear también el texto de búsqueda múltiple
      soloItemsConPLU: false, // También desactiva el filtro de solo PLU
    })
  }

  // Verificar si hay filtros activos para mostrar indicador
  const hasActiveFilters = selectedItem || selectedUbicacion || precioItem || soloStockBajo || mostrarUltimosCreados || mostrarRecetas || selectedProveedor

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        borderRadius: 2,
        position: "relative",
        overflow: "visible", // Cambiado de "hidden" a "visible"
        zIndex: 1, // Agregado z-index base
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 0,
        },
      }}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems="stretch">
        {/* Búsqueda de ítem */}
        <Box sx={{ flex: 1, minWidth: 280, position: "relative", zIndex: 10 }}>
          <Autocomplete
            size="small"
            disablePortal
            freeSolo
            options={(itemTresLetras || []).map((it) => ({
              label: generateItemLabel(it),
              descripcion: it.descripcion,
              codigo: it.codigo,
              id: it.id,
              codigoScanner: it.codigoScanner,
            }))}
            value={selectedItem}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                // Si es texto libre, actualizar searchText y limpiar selectedItem
                setSearchText(newValue);
                setSelectedItem(null);
              } else {
                // Si es un objeto (item seleccionado), manejarlo normalmente
                handleItemChange();
                setSelectedItem(newValue);
              }
            }}
            onInputChange={(event, newInputValue) => {
              setSearchText(newInputValue);
              // Si se borra el texto, limpiar también el item seleccionado
              if (!newInputValue || newInputValue.trim() === '') {
                setSelectedItem(null);
              }
            }}
            inputValue={searchText}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 9999, // Z-index alto para el dropdown
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: 200,
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar ítem (Enter para múltiples resultados)"
                variant="outlined"
                placeholder="Código, descripción o scanner..."
                onKeyPress={(event) => {
                  // Nueva funcionalidad: presionar Enter para búsqueda múltiple
                  if (event.key === 'Enter' && searchText && searchText.trim() !== "" && !selectedItem) {
                    event.preventDefault();
                    handleFilter();
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (selectedItem || (searchText && searchText.trim() !== "")) && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedItem(null);
                          setSearchText("");
                        }}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.background.paper,
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontSize: "0.875rem", py: 0.5 }}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>{option.descripcion}</Box>
                  <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    {option.codigo} • {option.codigoScanner}
                  </Box>
                </Box>
              </Box>
            )}
          />
        </Box>

        {/* Ubicación */}
        <Box sx={{ flex: 0.7, minWidth: 180, position: "relative", zIndex: 9 }}>
          <Autocomplete
            size="small"
            disablePortal
            options={ubicaciones.map((it) => ({
              id: it.id,
              label: it.descripcion,
            }))}
            value={selectedUbicacion}
            onChange={(e, newValue) => setSelectedUbicacion(newValue)}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 9998, // Z-index alto para el dropdown
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ubicación"
                variant="outlined"
                placeholder="Seleccionar..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <WarehouseIcon color="action" fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: selectedUbicacion && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSelectedUbicacion(null)}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.background.paper,
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        {/* Proveedor - Solo para inventario */}
        {vieneDe === "inventario" && (
          <Box sx={{ flex: 0.8, minWidth: 200, position: "relative", zIndex: 8 }}>
            <Autocomplete
              size="small"
              disablePortal
              options={entidadesClientesTresLetras.map((it) => ({
                label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                descripcion: it.descripcion,
                apellido: it.apellido,
                dniCuitCuil: it.dniCuitCuil,
                id: it.id,
              }))}
              value={selectedProveedor}
              onChange={(e, newValue) => setSelectedProveedor(newValue)}
              onInputChange={(e, newInputValue) => setSearchTextProveedor(newInputValue)}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 9997,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor"
                  variant="outlined"
                  placeholder="Buscar proveedor..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: selectedProveedor && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedProveedor(null)}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: alpha(theme.palette.background.paper, 0.7),
                      "&:hover": {
                        backgroundColor: theme.palette.background.paper,
                      },
                      "&.Mui-focused": {
                        backgroundColor: theme.palette.background.paper,
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        )}

        {/* Opciones de filtrado */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", lg: "row" },
            gap: 1,
            alignItems: "center",
            minWidth: "fit-content",
          }}
        >


        </Box>

        {/* Botones de acción y estado */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: "fit-content" }}>
          {hasActiveFilters && (
            <Chip
              label={`${
                [selectedItem, selectedUbicacion, precioItem, soloStockBajo, mostrarUltimosCreados, mostrarRecetas, selectedProveedor].filter(Boolean)
                  .length
              }`}
              size="small"
              color="primary"
              variant="filled"
              sx={{
                fontSize: "0.7rem",
                height: 24,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleFilter}
            startIcon={<FilterAltIcon fontSize="small" />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              backgroundColor: "#5b5b5f",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 2,
              py: 0.75,
              minWidth: "fit-content",
              boxShadow: "0 2px 8px rgba(91, 91, 95, 0.25)",
              "&:hover": {
                backgroundColor: theme.palette.secondary.main,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(91, 91, 95, 0.35)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {vieneDe == "inventario" ? "Buscar" : "Agregar"}
          </Button>

          <Tooltip title="Limpiar filtros">
            <IconButton
              onClick={handleReset}
              size="small"
              sx={{
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                borderRadius: 1.5,
                p: 0.75,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.15),
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  )
}

export default BuscadorItemsUbicaciones